import React from "react";
import { Circles } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/patientUtils";
import VisibilityIcon from "@mui/icons-material/Visibility";

interface ExaminationTabProps {
  examinationData: any[];
  loading: boolean;
  theme?: "light" | "dark";
}

const ExaminationTab: React.FC<ExaminationTabProps> = ({
  examinationData,
  loading,
  theme = "light",
}) => {
  const navigate = useNavigate();
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Circles height="30" width="30" color="#6366f1" ariaLabel="loading" />
      </div>
    );
  }

  if (examinationData.length === 0) {
    return (
      <div className={`text-center py-8 rounded-lg ${
        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
      }`}>
        <p className={theme === "dark" ? "text-slate-400" : "text-slate-500"}>No treatments found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h4 className={`text-sm font-semibold mb-3 ${
          theme === "dark" ? "text-slate-300" : "text-slate-700"
        }`}>Treatments List</h4>
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <div className="min-w-full inline-block align-middle">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className={`border-b ${
                  theme === "dark" ? "border-slate-700" : "border-slate-200"
                }`}>
                  <th className={`text-left py-2 px-2 sm:px-3 text-[10px] sm:text-xs font-semibold uppercase ${
                    theme === "dark" ? "text-slate-300" : "text-slate-700"
                  }`}>Date</th>
                  <th className={`text-left py-2 px-2 sm:px-3 text-[10px] sm:text-xs font-semibold uppercase ${
                    theme === "dark" ? "text-slate-300" : "text-slate-700"
                  }`}>Treatment</th>
                  <th className={`text-left py-2 px-2 sm:px-3 text-[10px] sm:text-xs font-semibold uppercase ${
                    theme === "dark" ? "text-slate-300" : "text-slate-700"
                  }`}>Note</th>
                  <th className={`text-left py-2 px-2 sm:px-3 text-[10px] sm:text-xs font-semibold uppercase ${
                    theme === "dark" ? "text-slate-300" : "text-slate-700"
                  }`}>Dentist</th>
                  <th className={`text-left py-2 px-2 sm:px-3 text-[10px] sm:text-xs font-semibold uppercase ${
                    theme === "dark" ? "text-slate-300" : "text-slate-700"
                  }`}>Status</th>
                  <th className={`text-center py-2 px-2 sm:px-3 text-[10px] sm:text-xs font-semibold uppercase ${
                    theme === "dark" ? "text-slate-300" : "text-slate-700"
                  }`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {examinationData.map((item: any, i: number) =>
                  item?.treatments?.map((treatment: any, j: number) => (
                    <tr key={`${i}-${j}`} className={`border-b ${
                      theme === "dark" ? "border-slate-700/50 hover:bg-slate-700/30" : "border-slate-100 hover:bg-slate-50"
                    } transition-colors`}>
                      <td className={`py-2 px-2 sm:px-3 text-[10px] sm:text-xs ${
                        theme === "dark" ? "text-slate-300" : "text-slate-600"
                      }`}>
                        {formatDate(treatment?.createdAt)}
                      </td>
                      <td className={`py-2 px-2 sm:px-3 text-[10px] sm:text-xs font-medium ${
                        theme === "dark" ? "text-slate-200" : "text-slate-800"
                      }`}>
                        {treatment?.treatment}
                        {treatment?.type ? ` - ${treatment?.type}` : ""}
                        {treatment?.subtype ? ` - ${treatment?.subtype}` : ""}
                      </td>
                      <td className={`py-2 px-2 sm:px-3 text-[10px] sm:text-xs max-w-[120px] sm:max-w-[150px] truncate ${
                        theme === "dark" ? "text-slate-400" : "text-slate-500"
                      }`}>
                        {treatment?.note || "N/A"}
                      </td>
                      <td className={`py-2 px-2 sm:px-3 text-[10px] sm:text-xs ${
                        theme === "dark" ? "text-slate-300" : "text-slate-600"
                      }`}>
                        {treatment?.didBy
                          ? `${treatment?.didBy?.firstName || ""} ${treatment?.didBy?.lastName || ""}`.trim()
                          : "N/A"}
                      </td>
                      <td className="py-2 px-2 sm:px-3">
                        <span className={`px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-medium ${
                          treatment?.status === "Complete"
                            ? theme === "dark"
                              ? "bg-emerald-900/30 text-emerald-300"
                              : "bg-emerald-100 text-emerald-700"
                            : treatment?.status === "ongoing"
                            ? theme === "dark"
                              ? "bg-amber-900/30 text-amber-300"
                              : "bg-amber-100 text-amber-700"
                            : theme === "dark"
                            ? "bg-slate-700 text-slate-300"
                            : "bg-slate-100 text-slate-600"
                        }`}>
                          {treatment?.status || "N/A"}
                        </span>
                      </td>
                      <td className="py-2 px-2 sm:px-3 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item?._id) {
                              navigate(`/client/treatment-diagnose/add-treatment/diagnose/${item._id}`);
                            }
                          }}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                            theme === "dark"
                              ? "bg-indigo-900/30 text-indigo-300 hover:bg-indigo-900/50 focus:ring-indigo-500"
                              : "bg-indigo-50 text-indigo-600 hover:bg-indigo-100 active:bg-indigo-200 focus:ring-indigo-500"
                          }`}
                          title="View Treatment"
                          aria-label="View treatment details"
                        >
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExaminationTab;

